import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../../components/layout/Layout';
import { useEmpresa } from '../../../contexts/EmpresaContext';

export default function IncentivoDashboard() {
  const { t } = useTranslation();
  const { empresaSelecionada } = useEmpresa();
  const [selectedPeriod, setSelectedPeriod] = useState('mes');

  // Recarregar dados quando a empresa mudar
  useEffect(() => {
    carregarDadosEmpresa();
  }, [empresaSelecionada.id]);

  const carregarDadosEmpresa = () => {
    // Aqui você carregaria os dados reais da empresa selecionada
    console.log('Carregando dashboard da empresa:', empresaSelecionada.id, empresaSelecionada.nome);
    // Exemplo: buscar estatísticas da API baseado no empresaSelecionada.id
  };

  const stats = [
    {
      title: t('dashboard.activeCampaigns'),
      value: '12',
      change: '+3',
      changeType: 'positive',
      icon: 'ri-megaphone-line',
      color: 'blue'
    },
    {
      title: t('dashboard.activeParticipants'),
      value: '1.247',
      change: '+156',
      changeType: 'positive',
      icon: 'ri-group-line',
      color: 'emerald'
    },
    {
      title: t('dashboard.distributedPoints'),
      value: '89.432',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'ri-coin-line',
      color: 'orange'
    },
    {
      title: t('dashboard.redeemedRewards'),
      value: '234',
      change: '+18',
      changeType: 'positive',
      icon: 'ri-gift-line',
      color: 'slate'
    }
  ];

  const topCampaigns = [
    { name: 'Vendas Q4 2024', participants: 156, points: 12450, status: t('dashboard.active') },
    { name: 'Meta Mensal Dezembro', participants: 89, points: 8900, status: t('dashboard.active') },
    { name: 'Indicação de Clientes', participants: 234, points: 15600, status: t('dashboard.active') },
    { name: 'Treinamento Produto', participants: 67, points: 4500, status: t('dashboard.paused') }
  ];

  const topParticipants = [
    { name: 'Maria Santos', points: 2450, position: 1, avatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20portrait%20with%20clean%20background%2C%20corporate%20style%2C%20friendly%20expression%2C%20high%20quality%20headshot&width=40&height=40&seq=participant-1&orientation=squarish' },
    { name: 'João Silva', points: 2340, position: 2, avatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20portrait%20with%20clean%20background%2C%20corporate%20style%2C%20confident%20expression%2C%20high%20quality%20headshot&width=40&height=40&seq=participant-2&orientation=squarish' },
    { name: 'Ana Costa', points: 2180, position: 3, avatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20portrait%20with%20clean%20background%2C%20corporate%20style%2C%20smiling%20expression%2C%20high%20quality%20headshot&width=40&height=40&seq=participant-3&orientation=squarish' },
    { name: 'Carlos Lima', points: 1950, position: 4, avatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20portrait%20with%20clean%20background%2C%20corporate%20style%2C%20friendly%20expression%2C%20high%20quality%20headshot&width=40&height=40&seq=participant-4&orientation=squarish' }
  ];

  const getStatColor = (color: string) => {
    const colors = {
      blue: 'bg-blue-600',
      emerald: 'bg-emerald-500',
      orange: 'bg-orange-500',
      slate: 'bg-slate-600'
    };
    return colors[color as keyof typeof colors];
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
            <p className="text-gray-600 mt-1">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="semana">{t('dashboard.thisWeek')}</option>
              <option value="mes">{t('dashboard.thisMonth')}</option>
              <option value="trimestre">{t('dashboard.thisQuarter')}</option>
              <option value="ano">{t('dashboard.thisYear')}</option>
            </select>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line"></i>
                </div>
                <span>{t('dashboard.newCampaign')}</span>
              </div>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {stat.change}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">{t('dashboard.vsPreviousPeriod')}</span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-lg ${getStatColor(stat.color)} flex items-center justify-center`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${stat.icon} text-white text-xl`}></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts and Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Campanhas */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.featuredCampaigns')}</h3>
              <button className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer whitespace-nowrap">
                {t('dashboard.viewAll')}
              </button>
            </div>
            <div className="space-y-4">
              {topCampaigns.map((campaign, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{campaign.name}</h4>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-gray-600">
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-group-line"></i>
                        </div>
                        {campaign.participants} {t('dashboard.participants')}
                      </span>
                      <span className="text-sm text-gray-600">
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-coin-line"></i>
                        </div>
                        {campaign.points.toLocaleString()} {t('dashboard.points').toLowerCase()}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    campaign.status === t('dashboard.active')
                      ? 'bg-emerald-100 text-emerald-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {campaign.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Participantes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.participantsRanking')}</h3>
              <button className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer whitespace-nowrap">
                {t('dashboard.viewFullRanking')}
              </button>
            </div>
            <div className="space-y-4">
              {topParticipants.map((participant, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-medium text-gray-600">
                    {participant.position}
                  </div>
                  <img
                    src={participant.avatar}
                    alt={participant.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{participant.name}</p>
                    <p className="text-sm text-gray-600">{participant.points.toLocaleString()} {t('dashboard.points').toLowerCase()}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-trophy-line text-orange-500"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gráfico de Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.campaignPerformance')}</h3>
            <div className="flex items-center space-x-2">
              <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg cursor-pointer whitespace-nowrap">
                {t('dashboard.points')}
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">
                {t('dashboard.participation')}
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">
                {t('dashboard.redemptions')}
              </button>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-bar-chart-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-gray-600">{t('dashboard.chartPlaceholder')}</p>
              <p className="text-sm text-gray-500 mt-1">{t('dashboard.chartSubtitle')}</p>
            </div>
          </div>
        </div>

        {/* Ações Rápidas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('dashboard.quickActions')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-megaphone-line text-blue-600"></i>
                </div>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">{t('dashboard.createCampaign')}</p>
                <p className="text-sm text-gray-600">{t('dashboard.newIncentiveCampaign')}</p>
              </div>
            </button>
            
            <button className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-coin-line text-orange-600"></i>
                </div>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">{t('dashboard.distributePoints')}</p>
                <p className="text-sm text-gray-600">{t('dashboard.sendPointsToParticipants')}</p>
              </div>
            </button>
            
            <button className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-chart-line text-slate-600"></i>
                </div>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">{t('dashboard.generateReport')}</p>
                <p className="text-sm text-gray-600">{t('dashboard.performanceReport')}</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}